import { useCallback } from 'react';
import {
  getApiProjectsProjectIdPeopleQueryKey,
  getApiProjectsProjectIdPeopleQueryOptions,
  getApiProjectsProjectIdPeopleTeamMemberIdQueryOptions,
  useGetApiProjectsProjectIdPeople,
  useGetApiProjectsProjectIdPeopleTeamMemberId,
  useGetApiProjectsProjectIdTeamsTeamIdMembersTeamMemberIdIssueDependencies,
  usePatchApiProjectsProjectIdTeamsTeamIdMembersTeamMemberId,
  usePostApiProjectsProjectIdTeamJoinTokensTokenJoin,
  usePostApiProjectsProjectIdTeamsTeamIdMembersTeamMemberIdArchive,
} from '@shape-construction/api/src/hooks';
import {
  type IssueGroupCountEntitySchema,
  type TeamMemberSchema,
  type TeamSchema,
  teamMemberRoleEnum,
  teamMemberStatusEnum,
} from '@shape-construction/api/src/types';
import { queryOptions, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useCurrentUser } from 'app/contexts/user/useCurrentUser';

// queries
export const useProjectPeople = useGetApiProjectsProjectIdPeople;
export const getProjectPeopleQueryOptions = getApiProjectsProjectIdPeopleQueryOptions;

export const useProjectPersonDependencies = useGetApiProjectsProjectIdTeamsTeamIdMembersTeamMemberIdIssueDependencies;

export const useProjectPerson = useGetApiProjectsProjectIdPeopleTeamMemberId;
export const getProjectPersonQueryOptions = getApiProjectsProjectIdPeopleTeamMemberIdQueryOptions;

export const useProjectMeQueryOptions = (...args: Parameters<typeof getProjectPeopleQueryOptions>) => {
  const [projectId, params] = args;
  const currentUser = useCurrentUser();

  return queryOptions({
    ...getProjectPeopleQueryOptions(projectId, params),
    select: (data) => data.find(({ user }) => user.id === currentUser?.id),
  });
};

export const getProjectPersonByIdQueryOptions = (
  projectId: Parameters<typeof getProjectPeopleQueryOptions>[0],
  teamMemberId?: TeamMemberSchema['id']
) =>
  queryOptions({
    ...getProjectPeopleQueryOptions(projectId),
    select: (data) => data.find((person) => person.id === teamMemberId),
    enabled: !!projectId && teamMemberId != null,
  });

export const useProjectPersonGetter = (
  projectId: Parameters<typeof useProjectPeople>[0],
  params?: Parameters<typeof useProjectPeople>[1]
) => {
  const { data } = useSuspenseQuery(getProjectPeopleQueryOptions(projectId, params));

  return useCallback(
    (memberTeamId: IssueGroupCountEntitySchema['identifier']) => data?.find(({ id }) => id === memberTeamId),
    [data]
  );
};

export const useTeamMembers = (
  projectId: Parameters<typeof useProjectPeople>[0],
  teamId: TeamSchema['id'],
  status: TeamMemberSchema['status'] | Array<TeamMemberSchema['status']>
) =>
  useProjectPeople(
    projectId,
    { team_id: teamId, status },
    {
      query: {
        select: (data) => data.sort((personA, personB) => personA.user.name.localeCompare(personB.user.name)),
      },
    }
  );

// mutations
export const useUpdateTeamMember = () => {
  const queryClient = useQueryClient();

  return usePatchApiProjectsProjectIdTeamsTeamIdMembersTeamMemberId({
    mutation: {
      onSettled: (_, __, variables) => {
        queryClient.invalidateQueries({
          queryKey: getApiProjectsProjectIdPeopleQueryKey(variables.projectId),
        });
      },
    },
  });
};

export const useArchiveTeamMember = () => {
  const queryClient = useQueryClient();

  return usePostApiProjectsProjectIdTeamsTeamIdMembersTeamMemberIdArchive({
    mutation: {
      onSettled: (_, __, variables) => {
        queryClient.invalidateQueries({
          queryKey: getApiProjectsProjectIdPeopleQueryKey(variables.projectId),
        });
      },
    },
  });
};

export const useTeamMembersCreate = usePostApiProjectsProjectIdTeamJoinTokensTokenJoin;

// selectors

// issues can be assigned to people who are invited but haven't joined the project yet.
const joinedAndInvitedContributerParams: NonNullable<Parameters<typeof useProjectPeople>>[1] = {
  status: [teamMemberStatusEnum.joined, teamMemberStatusEnum.invited],
  role: [teamMemberRoleEnum.contributor, teamMemberRoleEnum.admin, teamMemberRoleEnum.owner],
};

export const getJoinedAndInvitedContributorsQueryOptions = (
  ...args: Parameters<typeof getProjectPeopleQueryOptions>
) => {
  const [projectId] = args;
  return getProjectPeopleQueryOptions(projectId, joinedAndInvitedContributerParams);
};

const joinedContributorParams: NonNullable<Parameters<typeof useProjectPeople>>[1] = {
  status: teamMemberStatusEnum.joined,
  role: [teamMemberRoleEnum.contributor, teamMemberRoleEnum.admin, teamMemberRoleEnum.owner],
};

export const getProjectContributorsQueryOptions = (...args: Parameters<typeof getProjectPeopleQueryOptions>) => {
  const [projectId] = args;
  return getProjectPeopleQueryOptions(projectId, joinedContributorParams);
};
